import styled from 'styled-components'
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded'
import GasMeterRoundedIcon from '@mui/icons-material/GasMeterRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import FoundationRoundedIcon from '@mui/icons-material/FoundationRounded'
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded'
import DangerousRoundedIcon from '@mui/icons-material/DangerousRounded'
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded'
import HealthAndSafetyRoundedIcon from '@mui/icons-material/HealthAndSafetyRounded'
import ElevatorRoundedIcon from '@mui/icons-material/ElevatorRounded'
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import GavelRoundedIcon from '@mui/icons-material/GavelRounded'
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded'
import EngineeringRoundedIcon from '@mui/icons-material/EngineeringRounded'
import {
  incidentCategoryLabel,
  STATUS_META,
  INCIDENT_TYPE_META,
  INVESTIGATION_STATUS_META,
} from 'features/incidents/constants'
import SeverityQuickEdit from 'components/SeverityQuickEdit'

const CATEGORY_META = {
  fire:       { Icon: LocalFireDepartmentRoundedIcon, colour: '#c2410c', bg: '#ffedd5' },
  gas:        { Icon: GasMeterRoundedIcon,            colour: '#d97706', bg: '#fef3c7' },
  electrical: { Icon: BoltRoundedIcon,                colour: '#b45309', bg: '#fef9c3' },
  structural: { Icon: FoundationRoundedIcon,          colour: '#57534e', bg: '#e7e5e4' },
  water:      { Icon: WaterDropRoundedIcon,           colour: '#0369a1', bg: '#e0f2fe' },
  asbestos:   { Icon: DangerousRoundedIcon,           colour: '#b91c1c', bg: '#fee2e2' },
  security:   { Icon: SecurityRoundedIcon,            colour: '#4f46e5', bg: '#e0e7ff' },
  health:     { Icon: HealthAndSafetyRoundedIcon,     colour: '#15803d', bg: '#dcfce7' },
  lift:       { Icon: ElevatorRoundedIcon,            colour: '#7c3aed', bg: '#ede9fe' },
  other:      { Icon: ReportProblemRoundedIcon,       colour: '#64748b', bg: '#e2e8f0' },
}

const Row = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  cursor: pointer;
  background: white;
  &:hover { background: ${({ theme }) => theme.colors.lightGrey}; }
  &:last-child { border-bottom: none; }
`
const Body = styled.div`
  flex: 1;
  min-width: 0;
`
const TitleLine = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const TypeTag = styled.span`
  display: inline-block;
  padding: 1px 7px;
  margin-right: 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  vertical-align: middle;
  color: ${({ $colour }) => $colour};
  background: ${({ $bg }) => $bg};
`
const CategoryBadge = styled.span`
  display: inline-block;
  font-weight: 600;
  margin-right: 8px;
`
const NarrativeSnippet = styled.span`
  color: #475569;
`
const Meta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #64748b;
  flex-wrap: wrap;
`
const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: ${({ $bg }) => $bg || '#e2e8f0'};
  color: ${({ $colour }) => $colour || '#475569'};
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`
const CatIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  flex-shrink: 0;
  color: ${({ $colour }) => $colour};
  background: ${({ $bg }) => $bg};
`
const Dot = styled.span`
  color: #cbd5e1;
`
const BpName = styled.span`
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  color: #334155;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const ContractorTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-weight: 600;
  color: #334155;
`

const formatDate = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

const IncidentRow = ({ incident, onClick, onSeverityChange }) => {
  const stat = STATUS_META[incident.status]
  const summary = (incident.narrative || '').slice(0, 90).trim()
  const truncated = (incident.narrative || '').length > 90
  // Backend now sets actionsCount via withCount; falls back to actions.length when relation is loaded.
  const actionsCount = incident.actionsCount ?? (Array.isArray(incident.actions) ? incident.actions.length : 0)

  const typeMeta = INCIDENT_TYPE_META[incident.incidentType]
  const showType = incident.incidentType && incident.incidentType !== 'incident'

  const invMeta = INVESTIGATION_STATUS_META[incident.investigationStatus]
  const invOverdue = ['pending', 'in_progress'].includes(incident.investigationStatus)
    && incident.investigationDueAt
    && new Date(incident.investigationDueAt) < new Date()

  const riddorOn = !!incident.riddorReportable
  const riddorReported = !!incident.riddorReportedAt
  const riddorOverdue = riddorOn && !riddorReported && incident.riddorDeadline
    && new Date(incident.riddorDeadline) < new Date()

  const morOn = !!incident.morReportable
  const morReported = !!incident.morReportedAt
  const morOverdue = morOn && !morReported && incident.morDeadline
    && new Date(incident.morDeadline) < new Date()

  const cat = CATEGORY_META[incident.category] || CATEGORY_META.other
  const CatGlyph = cat.Icon
  const activeContractors = (incident.contractors || []).filter(c => !c.removed)

  return (
    <Row onClick={() => onClick?.(incident)}>
      <SeverityQuickEdit
        value={incident.severity}
        disabled={!onSeverityChange || !incident.permissions?.canUpdate}
        onChange={(next) => onSeverityChange?.(incident.id, next)}
      />
      <CatIcon $colour={cat.colour} $bg={cat.bg} title={incidentCategoryLabel(incident.category)}>
        <CatGlyph style={{ fontSize: 17 }} />
      </CatIcon>
      <Body>
        <TitleLine>
          {showType && typeMeta && (
            <TypeTag $colour={typeMeta.colour} $bg={typeMeta.bg}>{typeMeta.label}</TypeTag>
          )}
          <CategoryBadge>{incidentCategoryLabel(incident.category)}</CategoryBadge>
          <NarrativeSnippet>{summary}{truncated ? '…' : ''}</NarrativeSnippet>
        </TitleLine>
        <Meta>
          {stat && <StatusPill $colour={stat.colour} $bg={stat.bg}>{stat.label}</StatusPill>}
          {invMeta && (
            <StatusPill
              $colour={invOverdue ? '#b00020' : invMeta.colour}
              $bg={invOverdue ? '#fde8e3' : invMeta.bg}>
              <SearchRoundedIcon style={{ fontSize: 12 }} />
              {invOverdue ? 'Investigation overdue' : invMeta.label}
            </StatusPill>
          )}
          {riddorOn && (
            <StatusPill
              title={incident.riddorDeadline ? `RIDDOR deadline ${formatDate(incident.riddorDeadline)}` : 'RIDDOR reportable'}
              $colour={riddorReported ? '#15803d' : riddorOverdue ? '#b00020' : '#d97706'}
              $bg={riddorReported ? '#dcfce7' : riddorOverdue ? '#fde8e3' : '#fef3c7'}>
              <GavelRoundedIcon style={{ fontSize: 12 }} />
              {riddorReported ? 'RIDDOR reported' : riddorOverdue ? 'RIDDOR overdue' : 'RIDDOR'}
            </StatusPill>
          )}
          {morOn && (
            <StatusPill
              title={incident.morDeadline ? `Mandatory Occurrence Report to the Building Safety Regulator — deadline ${formatDate(incident.morDeadline)}` : 'Mandatory Occurrence — reportable to the Building Safety Regulator'}
              $colour={morReported ? '#15803d' : morOverdue ? '#b00020' : '#c2410c'}
              $bg={morReported ? '#dcfce7' : morOverdue ? '#fde8e3' : '#ffedd5'}>
              <ApartmentRoundedIcon style={{ fontSize: 12 }} />
              {morReported ? 'MOR reported' : morOverdue ? 'MOR overdue' : 'MOR'}
            </StatusPill>
          )}
          {incident.buildingPassport?.name && (
            <>
              <BpName title={incident.buildingPassport.name}>{incident.buildingPassport.name}</BpName>
              <Dot>·</Dot>
            </>
          )}
          <span>{formatDate(incident.occurredAt)}</span>
          {incident.investigatorName && (
            <>
              <Dot>·</Dot>
              <span>Investigator: {incident.investigatorName}</span>
            </>
          )}
          {actionsCount > 0 && (
            <>
              <Dot>·</Dot>
              <span>{actionsCount} remedial{actionsCount === 1 ? '' : 's'}</span>
            </>
          )}
          {activeContractors.length > 0 && (
            <>
              <Dot>·</Dot>
              <ContractorTag title={activeContractors.map(c => c.name).join(', ')}>
                <EngineeringRoundedIcon style={{ fontSize: 12 }} />
                {activeContractors.length === 1 ? activeContractors[0].name : `${activeContractors.length} contractors`}
              </ContractorTag>
            </>
          )}
        </Meta>
      </Body>
    </Row>
  )
}

export default IncidentRow
